import React, { useState, useEffect } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { InputMask } from "primereact/inputmask";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { Message } from "primereact/message";
import { WeekSchedule, DaySchedule } from "@types";

interface HoursScheduleProps {
  value?: WeekSchedule;
  onChange: (schedule: WeekSchedule) => void;
  disabled?: boolean;
  label?: string;
}

const DAYS = [
  { key: "monday", label: "Monday", short: "MON" },
  { key: "tuesday", label: "Tuesday", short: "TUE" },
  { key: "wednesday", label: "Wednesday", short: "WED" },
  { key: "thursday", label: "Thursday", short: "THU" },
  { key: "friday", label: "Friday", short: "FRI" },
  { key: "saturday", label: "Saturday", short: "SAT" },
  { key: "sunday", label: "Sunday", short: "SUN" },
] as const;

const DEFAULT_DAY_SCHEDULE: DaySchedule = {
  isOpen: true,
  openTime: "09:00",
  closeTime: "17:00",
  is24Hours: false,
};

const DEFAULT_WEEK_SCHEDULE: WeekSchedule = {
  monday: { ...DEFAULT_DAY_SCHEDULE },
  tuesday: { ...DEFAULT_DAY_SCHEDULE },
  wednesday: { ...DEFAULT_DAY_SCHEDULE },
  thursday: { ...DEFAULT_DAY_SCHEDULE },
  friday: { ...DEFAULT_DAY_SCHEDULE },
  saturday: { ...DEFAULT_DAY_SCHEDULE, isOpen: false },
  sunday: { ...DEFAULT_DAY_SCHEDULE, isOpen: false },
};

export function HoursSchedule({
  value,
  onChange,
  disabled = false,
  label = "Business Hours",
}: HoursScheduleProps) {
  const [schedule, setSchedule] = useState<WeekSchedule>(
    value || DEFAULT_WEEK_SCHEDULE
  );
  const [selectedDay, setSelectedDay] = useState<string>("monday");

  useEffect(() => {
    if (value) {
      setSchedule(value);
    }
  }, [value]);

  const handleDayChange = (
    day: keyof WeekSchedule,
    field: keyof DaySchedule,
    value: any
  ) => {
    const updatedSchedule = {
      ...schedule,
      [day]: {
        ...schedule[day],
        [field]: value,
      },
    };
    setSchedule(updatedSchedule);
    // Only trigger onChange for actual schedule changes, not tab navigation
    onChange(updatedSchedule);
  };

  const applyToAllDays = () => {
    const currentDaySchedule = schedule[selectedDay as keyof WeekSchedule];
    const updatedSchedule = DAYS.reduce((acc, day) => ({
      ...acc,
      [day.key]: { ...currentDaySchedule },
    }), {} as WeekSchedule);

    setSchedule(updatedSchedule);
    onChange(updatedSchedule);
  };

  const applyToWeekdays = () => {
    const currentDaySchedule = schedule[selectedDay as keyof WeekSchedule];
    const weekdays = ["monday", "tuesday", "wednesday", "thursday", "friday"];
    const updatedSchedule = { ...schedule };

    weekdays.forEach((day) => {
      updatedSchedule[day as keyof WeekSchedule] = { ...currentDaySchedule };
    });

    setSchedule(updatedSchedule);
    onChange(updatedSchedule);
  };

  const applyToWeekends = () => {
    const currentDaySchedule = schedule[selectedDay as keyof WeekSchedule];
    const weekends = ["saturday", "sunday"];
    const updatedSchedule = { ...schedule };

    weekends.forEach((day) => {
      updatedSchedule[day as keyof WeekSchedule] = { ...currentDaySchedule };
    });

    setSchedule(updatedSchedule);
    onChange(updatedSchedule);
  };

  const formatTimeDisplay = (time?: string) => {
    if (!time) return "--:--";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${period}`;
  };

  const currentDaySchedule = schedule[selectedDay as keyof WeekSchedule];

  const getDayStatus = (day: DaySchedule) => {
    if (!day.isOpen) return { label: "Closed", color: "#ef4444", icon: "pi-times-circle" };
    if (day.is24Hours) return { label: "24/7", color: "#10b981", icon: "pi-clock" };
    return { label: "Open", color: "#3b82f6", icon: "pi-check-circle" };
  };

  const setQuickHours = (type: string) => {
    let newSchedule: DaySchedule;

    switch(type) {
      case "business":
        newSchedule = { isOpen: true, openTime: "09:00", closeTime: "17:00", is24Hours: false };
        break;
      case "extended":
        newSchedule = { isOpen: true, openTime: "06:00", closeTime: "22:00", is24Hours: false };
        break;
      case "24hours":
        newSchedule = { isOpen: true, openTime: "00:00", closeTime: "23:59", is24Hours: true };
        break;
      case "closed":
        newSchedule = { isOpen: false, openTime: undefined, closeTime: undefined, is24Hours: false };
        break;
      default:
        return;
    }

    // Update all fields at once with the new schedule
    const updatedSchedule = {
      ...schedule,
      [selectedDay]: newSchedule
    };
    setSchedule(updatedSchedule);
    onChange(updatedSchedule);
  };

  return (
    <div className="w-full">
      <div className="surface-card border-round-lg shadow-1">
        {/* Header */}
        <div className="p-3 border-bottom-1 surface-border">
          <div className="flex align-items-center justify-content-between">
            <h5 className="m-0 text-900 font-semibold">
              <i className="pi pi-calendar mr-2 text-primary" />
              {label}
            </h5>
            <div className="flex gap-2">
              <Button
                icon="pi pi-copy"
                severity="info"
                size="small"
                rounded
                text
                tooltip="Apply to all days"
                onClick={applyToAllDays}
                disabled={disabled}
              />
              <Button
                icon="pi pi-briefcase"
                severity="warning"
                size="small"
                rounded
                text
                tooltip="Apply to weekdays"
                onClick={applyToWeekdays}
                disabled={disabled}
              />
              <Button
                icon="pi pi-sun"
                severity="success"
                size="small"
                rounded
                text
                tooltip="Apply to weekends"
                onClick={applyToWeekends}
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        {/* Day Selector Tabs */}
        <div className="p-3 border-bottom-1 surface-border bg-gray-50">
          <div className="flex gap-2 flex-wrap">
            {DAYS.map((day) => {
              const daySchedule = schedule[day.key as keyof WeekSchedule];
              const status = getDayStatus(daySchedule);
              const isSelected = selectedDay === day.key;

              return (
                <button
                  key={day.key}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedDay(day.key);
                  }}
                  type="button"
                  className={`
                    px-3 py-2 border-round-lg border-1 transition-all-300 cursor-pointer
                    ${isSelected
                      ? "bg-primary border-primary text-white shadow-2"
                      : "bg-white border-200 hover:border-primary hover:shadow-1"
                    }
                  `}
                  style={{ minWidth: "80px" }}
                  disabled={disabled}
                >
                  <div className="text-center">
                    <div className={`text-xs font-bold ${isSelected ? "" : "text-600"}`}>
                      {day.short}
                    </div>
                    <div className={`text-xs mt-1 ${isSelected ? "" : "text-500"}`}>
                      <i className={`pi ${status.icon} mr-1`} style={{
                        fontSize: "0.7rem",
                        color: isSelected ? "white" : status.color
                      }} />
                      {status.label}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Day Configuration */}
        <div className="p-4">
          <div className="mb-3">
            <h6 className="text-900 font-semibold mb-3">
              {DAYS.find(d => d.key === selectedDay)?.label} Schedule
            </h6>
          </div>

          {/* Quick Templates */}
          <div className="mb-4">
            <div className="text-sm text-600 mb-2">Quick Templates:</div>
            <div className="flex gap-2 flex-wrap">
              <Chip
                label="Business Hours (9-5)"
                className="cursor-pointer"
                onClick={() => setQuickHours("business")}
              />
              <Chip
                label="Extended (6am-10pm)"
                className="cursor-pointer"
                onClick={() => setQuickHours("extended")}
              />
              <Chip
                label="24 Hours"
                className="cursor-pointer"
                onClick={() => setQuickHours("24hours")}
              />
              <Chip
                label="Closed"
                className="cursor-pointer"
                onClick={() => setQuickHours("closed")}
              />
            </div>
          </div>

          {/* Open/Closed Toggle */}
          <div className="flex align-items-center gap-3 mb-3 p-3 surface-100 border-round-lg">
            <span className="font-semibold text-900">Status:</span>
            <InputSwitch
              checked={currentDaySchedule.isOpen}
              onChange={(e) => handleDayChange(selectedDay as keyof WeekSchedule, "isOpen", e.value)}
              disabled={disabled}
            />
            <span className={`font-semibold ${currentDaySchedule.isOpen ? "text-green-600" : "text-red-600"}`}>
              {currentDaySchedule.isOpen ? "OPEN" : "CLOSED"}
            </span>
          </div>

          {/* Time Configuration */}
          {currentDaySchedule.isOpen && (
            <div className="mt-3">
              {/* 24 Hours Toggle */}
              <div className="flex align-items-center gap-3 mb-3 p-3 surface-100 border-round-lg">
                <span className="font-semibold text-900">24 Hour Service:</span>
                <InputSwitch
                  checked={currentDaySchedule.is24Hours || false}
                  onChange={(e) => {
                    handleDayChange(selectedDay as keyof WeekSchedule, "is24Hours", e.value);
                    if (e.value) {
                      handleDayChange(selectedDay as keyof WeekSchedule, "openTime", "00:00");
                      handleDayChange(selectedDay as keyof WeekSchedule, "closeTime", "23:59");
                    }
                  }}
                  disabled={disabled}
                />
                {currentDaySchedule.is24Hours && (
                  <Chip label="Always Open" icon="pi pi-sun" className="bg-yellow-100 text-yellow-800" />
                )}
              </div>

              {/* Time Inputs */}
              {!currentDaySchedule.is24Hours && (
                <div className="p-3 surface-100 border-round-lg">
                  <div className="grid">
                    <div className="col-12 md:col-6">
                      <label className="block text-900 font-semibold mb-2">
                        <i className="pi pi-sign-in mr-2 text-green-600" />
                        Opening Time
                      </label>
                      <InputMask
                        value={currentDaySchedule.openTime}
                        onChange={(e) => handleDayChange(selectedDay as keyof WeekSchedule, "openTime", e.value)}
                        mask="99:99"
                        placeholder="HH:MM"
                        className="w-full"
                        disabled={disabled}
                      />
                      <small className="text-600">24-hour format (e.g., 09:00 for 9 AM)</small>
                    </div>
                    <div className="col-12 md:col-6">
                      <label className="block text-900 font-semibold mb-2">
                        <i className="pi pi-sign-out mr-2 text-red-600" />
                        Closing Time
                      </label>
                      <InputMask
                        value={currentDaySchedule.closeTime}
                        onChange={(e) => handleDayChange(selectedDay as keyof WeekSchedule, "closeTime", e.value)}
                        mask="99:99"
                        placeholder="HH:MM"
                        className="w-full"
                        disabled={disabled}
                      />
                      <small className="text-600">24-hour format (e.g., 17:00 for 5 PM)</small>
                    </div>
                  </div>

                  {/* Time Display */}
                  {currentDaySchedule.openTime && currentDaySchedule.closeTime && (
                    <Message
                      severity="info"
                      text={`Hours: ${formatTimeDisplay(currentDaySchedule.openTime)} - ${formatTimeDisplay(currentDaySchedule.closeTime)}`}
                      className="mt-3"
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Week Overview */}
        <div className="p-3 border-top-1 surface-border bg-gray-50">
          <div className="text-sm text-600 mb-2 font-semibold">Week Overview:</div>
          <div className="flex gap-1 justify-content-between">
            {DAYS.map((day) => {
              const daySchedule = schedule[day.key as keyof WeekSchedule];
              const status = getDayStatus(daySchedule);

              return (
                <div
                  key={day.key}
                  className="text-center flex-1"
                  style={{ minWidth: "40px" }}
                >
                  <div className="text-xs text-600 mb-1">{day.short}</div>
                  <div
                    className="border-round p-1"
                    style={{ backgroundColor: status.color + "20" }}
                  >
                    <i
                      className={`pi ${status.icon}`}
                      style={{
                        fontSize: "0.8rem",
                        color: status.color
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}